'use client'

import { FunctionComponent, useMemo } from 'react'
import Image from 'next/image'
import classNames from 'classnames'
import { isBlackoutDateFromCode, isSpecialEventFromCode } from '@/utils/calendar.utils'
import globalStyles from '@/styles/templates/GlobalNotifications.module.css'
import StringToReactNode from '@/app/_components/StringToReactNode/StringToReactNode'
import styles from '../Alerts.module.css'
import { transformNotificationsToAnnouncements } from '@/utils/announcements.utils'
import { AnchorButton } from '../../AnchorButton'

/**
 * Announcements are in-page, non-dismissable alerts.
 */

type Props = {
  announcements: Array<Nullable<Transformed.Announcement>>
  notifications?: Transformed.Notification[]
  show: boolean
  isCalendarAnnouncement?: boolean
}

const Announcements: FunctionComponent<Props> = ({
  announcements,
  show,
  notifications,
  isCalendarAnnouncement = false,
}: Props) => {
  // TODO: these announcements should come from the server
  const combined = useMemo(() => ([
    ...announcements,
    ...transformNotificationsToAnnouncements(notifications),
  ]), [announcements, notifications])

  return (
    <div
      className={classNames(
        styles.AnnouncementsContainer,
        {
          [styles.ContainerActive]: show,
          [styles.HasChildren]: announcements.length > 0,
        },
      )}
      aria-atomic
      aria-live="polite"
    >
      {combined.length > 0 && combined.map((announcement) => {
        if (!announcement) return null
        const {
          priority,
          code,
          message,
          title,
          cta,
          ctaLink,
          svgImage,
          bgColor,
          borderColor,
          id,
        } = announcement as Transformed.AnnouncementWithNotificationData

        const isButtonLayout = !!cta && !!ctaLink

        return (
          <div
            data-testid="announcement"
            {...(isSpecialEventFromCode(code) && { 'data-special-event': true })}
            style={{
              '--border-color': borderColor,
              '--bg-color': bgColor,
            } as React.CSSProperties}
            className={classNames(
              {
                [globalStyles.Info]: priority === 'Cosmic' || priority === 'Low',
                [globalStyles.Warning]: priority === 'Medium',
                [globalStyles.Error]: priority === 'High',
                [globalStyles.ButtonLayout]: isButtonLayout,
                [globalStyles.Calendar]: isCalendarAnnouncement,
                [globalStyles.SpecialEvent]: isSpecialEventFromCode(code),
                [globalStyles.APBlackoutDay]: isBlackoutDateFromCode(code),
                [styles.AnnouncementActive]: show,
                [styles.Column]: title,
              },
              styles.Announcement,
              globalStyles.Alert,
            )}
            key={id}
          >
            {title && (
              <div className={styles.TitleContainer}>
                <span className={globalStyles.Icon} />
                <h5 className={styles.Title} data-testid="announcement-title">{title}</h5>
              </div>
            )}
            <div className={styles.IconWithContent} key={`icon-${id}`}>
              {svgImage && <div dangerouslySetInnerHTML={{ __html: svgImage }} className={styles.SvgContainer} />}
              {!title && !isButtonLayout && <span className={globalStyles.Icon} />}
              <StringToReactNode
                incomingString={message}
                className={classNames('content', { [styles.IndentContent]: title })}
              />
            </div>

            {isButtonLayout && (
              <AnchorButton href={ctaLink} className={styles.BannerBtn} size="md" key={`cta-${id}`}>
                {cta}
                <Image
                  width={13}
                  height={13}
                  className={styles.RightArrow}
                  src="/static/svg/right-arrow.svg"
                  alt=""
                />
              </AnchorButton>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default Announcements
